import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PropertyFilters = ({ onFiltersChange, className = "" }) => {
  const [filters, setFilters] = useState({
    location: '',
    propertyType: '',
    valuationStatus: '',
    dateRange: '',
    searchQuery: ''
  });

  const [isExpanded, setIsExpanded] = useState(true);

  const locationOptions = [
    { value: '', label: 'All Locations' },
    { value: 'downtown', label: 'Downtown District' },
    { value: 'suburbs', label: 'Suburban Areas' },
    { value: 'waterfront', label: 'Waterfront Properties' },
    { value: 'industrial', label: 'Industrial Zone' },
    { value: 'commercial', label: 'Commercial District' }
  ];

  const propertyTypeOptions = [
    { value: '', label: 'All Property Types' },
    { value: 'vacant plot', label: 'Vacant Plot' },
    { value: 'land', label: 'Land' },
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'commercial space', label: 'Commercial Space' },
    { value: 'flat', label: 'Flat' },
    { value: 'villa', label: 'Villa' },
    { value: 'under construction', label: 'Under Construction' }
  ];

  const valuationStatusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending Survey' },
    { value: 'in-progress', label: 'Survey In Progress' },
    { value: 'completed', label: 'Survey Completed' },
    { value: 'requires-update', label: 'Requires Update' },
    { value: 'expired', label: 'Valuation Expired' }
  ];

  const dateRangeOptions = [
    { value: '', label: 'All Time' },
    { value: 'last-7-days', label: 'Last 7 Days' },
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'last-90-days', label: 'Last 90 Days' },
    { value: 'last-year', label: 'Last Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      location: '',
      propertyType: '',
      valuationStatus: '',
      dateRange: '',
      searchQuery: ''
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value !== '');

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Filter Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">Property Filters</h3>
          {hasActiveFilters && (
            <span className="px-2 py-1 bg-primary text-primary-foreground text-xs rounded-full">
              Active
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              iconName="X"
              iconPosition="left"
            >
              Clear All
            </Button>
          )}
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsExpanded(!isExpanded)}
            className="lg:hidden"
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} />
          </Button>
        </div>
      </div>
      {/* Filter Content */}
      <div className={`p-4 ${!isExpanded ? 'hidden lg:block' : ''}`}>
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by address, MLS number, or client name..."
              value={filters?.searchQuery}
              onChange={(e) => handleFilterChange('searchQuery', e?.target?.value)}
              className="w-full pl-9 pr-9 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            {filters?.searchQuery && (
              <button onClick={() => handleFilterChange('searchQuery', '')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <Icon name="X" size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Filter Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select
            label="Location"
            options={locationOptions}
            value={filters?.location}
            onChange={(value) => handleFilterChange('location', value)}
            placeholder="Select location"
          />

          <Select
            label="Property Type"
            options={propertyTypeOptions}
            value={filters?.propertyType}
            onChange={(value) => handleFilterChange('propertyType', value)}
            placeholder="Select type"
          />

          <Select
            label="Valuation Status"
            options={valuationStatusOptions}
            value={filters?.valuationStatus}
            onChange={(value) => handleFilterChange('valuationStatus', value)}
            placeholder="Select status"
          />

          <Select
            label="Date Range"
            options={dateRangeOptions}
            value={filters?.dateRange}
            onChange={(value) => handleFilterChange('dateRange', value)}
            placeholder="Select range"
          />
        </div>

        {/* Quick Filter Buttons */}
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
          <span className="text-sm text-muted-foreground font-medium">Quick Filters:</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleFilterChange('valuationStatus', 'pending')}
            className={filters?.valuationStatus === 'pending' ? 'bg-primary text-primary-foreground' : ''}
          >
            Pending Surveys
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleFilterChange('valuationStatus', 'expired')}
            className={filters?.valuationStatus === 'expired' ? 'bg-primary text-primary-foreground' : ''}
          >
            Expired Valuations
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleFilterChange('dateRange', 'last-30-days')}
            className={filters?.dateRange === 'last-30-days' ? 'bg-primary text-primary-foreground' : ''}
          >
            Recent Activity
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PropertyFilters;